import { useState, useEffect } from "react";
import { fetchAgentDetails } from "../../../../services/agentService";
import { normalizeFiles } from "../../utils/fileNormalization";
import { debug } from "../../../../utils/debugLogger";
import { pickAgentTypeFromApi } from "../../../../constants/agentTypes";

interface UseAgentDetailsOptions {
  selectedAgentId: string;
  selectedCustomer: { customer_id: string } | null;
  idToken: string;
}

export interface ParsedAgentDetails {
  instructions: string;
  agentName: string;
  model: string;
  conversationStarters: Array<{ id: string; suggestion: string }>;
  profilePictureUrl: string;
  selectedTools: string[];
  agentType: string | undefined;
  knowledgeFiles: ReturnType<typeof normalizeFiles>;
  apiVersion: string;
  rawDetails: any;
}

export const useAgentDetails = (options: UseAgentDetailsOptions) => {
  const { selectedAgentId, selectedCustomer, idToken } = options;

  const [loading, setLoading] = useState<boolean>(false);
  const [agentDetails, setAgentDetails] = useState<any>(null);
  const [parsedDetails, setParsedDetails] = useState<ParsedAgentDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Ensure selectedAgentId is not empty string and all required values exist
    if (
      selectedAgentId &&
      selectedAgentId.trim() !== "" &&
      selectedCustomer &&
      selectedCustomer.customer_id &&
      idToken
    ) {
      debug.log("Fetching agent details for:", {
        selectedAgentId,
        customerId: selectedCustomer.customer_id,
      });
      setLoading(true);
      setError(null);

      fetchAgentDetails(selectedAgentId, selectedCustomer.customer_id, idToken)
        .then((details) => {
          debug.log("Agent details retrieved:", details);
          setAgentDetails(details);

          if (details) {
            // Extract tools from agent details
            let tools: string[] = [];
            if (details.tools) {
              if (Array.isArray(details.tools)) {
                // Handle different tool formats
                tools = details.tools
                  .map((tool: any) => {
                    if (typeof tool === "string") {
                      return tool;
                    } else if (tool && typeof tool === "object" && tool.name) {
                      return tool.name;
                    }
                    return null;
                  })
                  .filter((tool: string | null) => tool !== null) as string[];
              } else if (typeof details.tools === "object") {
                // Old format: tools is an object with boolean values
                tools = Object.keys(details.tools).filter(
                  (key) => details.tools[key] === true
                );
              }
            }

            // Load existing knowledge files
            const rawFiles = Array.isArray(details.training_files)
              ? details.training_files
              : [];
            const normalizedFiles = normalizeFiles(rawFiles);

            // Parse conversation starters
            const conversationStarters = Array.isArray(details.conversation_starters)
              ? details.conversation_starters.map((starter: any) => ({
                  id: starter.id || Date.now().toString() + Math.random(),
                  suggestion: starter.suggestion || "",
                }))
              : [];

            const parsed: ParsedAgentDetails = {
              instructions: details.instructions || "",
              agentName: details.agent_name || "",
              model: details.model || "",
              conversationStarters: conversationStarters,
              profilePictureUrl: details.profile_picture_url || "",
              selectedTools: tools,
              agentType: pickAgentTypeFromApi(details),
              knowledgeFiles: normalizedFiles,
              apiVersion: details.api_version || "",
              rawDetails: details,
            };

            setParsedDetails(parsed);
          } else {
            setParsedDetails(null);
          }
        })
        .catch((error) => {
          debug.error("Error fetching agent details:", error);
          setError("Failed to load agent details. Please try again.");
          setAgentDetails(null);
          setParsedDetails(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Clear when no agent is selected
      setAgentDetails(null);
      setParsedDetails(null);
      setError(null);
      setLoading(false);
    }
  }, [selectedAgentId, selectedCustomer, idToken]);

  return {
    loading,
    agentDetails,
    parsedDetails,
    error,
  };
};
